/* eslint-disable camelcase */
import React, { useEffect, useState } from "react";
import GenericModal from "@components/UI/organisms/Modal/GenericModal";
import {
  Box,
  Theme,
  Container,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Grid,
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FormCategoryTitle,
  FormSectionWrap,
} from "@components/UI/atoms/Forms/FormComponents";
import CircularLoading from "@components/UI/organisms/CircularLoading/CircularLoading";
import { useAppDispatch, useAppSelector } from "@root/hooks";
import { fetchLearningTemplatePackageDistributedStudent } from "@actions/superOwnerApp/templatePackages/learningTemplateHistory";
import { superOwnerAppLearningTemplateDistributionHistoryState } from "@reducers/superOwnerApp/templatePackages/learningTemplateHistory";
import { DistributedStudents, TtCsvExportType } from "@lib/Api";
import V2Pagination from "@components/UIv2/molecules/V2Pagination";
import { ReduxStatus } from "@constants/redux";
import SelectButton from "@components/UI/molecules/DownloadCsvFileListItem/SelectButton";

import { ITEMS_PER_PAGE } from "@constants/page";
import {
  LearningTemplateHistoryModalKind,
  LearningTemplateHistoryModalType,
} from "../../types";

interface Props {
  handleClose: () => void;
  modalState: LearningTemplateHistoryModalType;
}

enum GroupeBy {
  Grade = "grade",
  School = "school",
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonWrapper: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      marginBottom: theme.spacing(2),
      alignItems: "end",
      flexFlow: "wrap",
    },
    accordionRoot: {
      width: "100%",
    },
    accordionDetails: {
      display: "flex",
      flexWrap: "wrap",
      overflow: "auto",
    },
    studentInfosWrapper: {
      width: "100%",
      display: "flex",
      flexWrap: "wrap",
    },
  }),
);

const DistributionInfosModal: React.FC<Props> = ({
  handleClose,
  modalState,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { isOpen, kind, learningTemplatePackage } = modalState;
  const isInfoModalOpen =
    isOpen && kind === LearningTemplateHistoryModalKind.SHOW;
  const distributionId = learningTemplatePackage?.id as string;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const learningTemplateHistoryCache = useAppSelector(
    superOwnerAppLearningTemplateDistributionHistoryState,
  );
  const { distributedUsers, totalCountFetched, fetchUser } =
    learningTemplateHistoryCache;
  const handleGroupedData = (data: DistributedStudents[], type: GroupeBy) => {
    const accordionInfos = new Map<string, any[]>();
    data?.forEach((item: DistributedStudents) => {
      const groupe = type === GroupeBy.School ? item.company_name : item.grade;
      const key = groupe ?? "なし";
      const list = accordionInfos.get(key) ?? [];
      list.push(item);
      accordionInfos.set(key, list);
    });
    return accordionInfos;
  };

  const accordionMainInfos = handleGroupedData(
    distributedUsers as DistributedStudents[],
    GroupeBy.School,
  );

  const handlePageTransition = (newPage: number) => {
    dispatch(
      fetchLearningTemplatePackageDistributedStudent({
        id: distributionId,
        page: newPage,
        per_page: ITEMS_PER_PAGE,
      }),
    );
    setCurrentPage(newPage);
  };

  useEffect(() => {
    if (isInfoModalOpen) {
      dispatch(
        fetchLearningTemplatePackageDistributedStudent({
          id: distributionId,
          page: 1,
          per_page: ITEMS_PER_PAGE,
        }),
      );
    }
  }, [isInfoModalOpen]);

  return (
    <GenericModal
      useSecondaryButton
      title="配信先の情報"
      modalSize="sm"
      handleClose={handleClose}
      secondaryButtonAction={handleClose}
      secondaryButtonText="閉じる"
      open={isInfoModalOpen}
    >
      {fetchUser === ReduxStatus.pending ? (
        <CircularLoading />
      ) : (
        <Container>
          <FormSectionWrap className={classes.buttonWrapper}>
            <Box>
              <FormCategoryTitle>コンテンツ名</FormCategoryTitle>
              <Typography>
                {learningTemplatePackage?.learning_template_package?.title}
              </Typography>
            </Box>
            <SelectButton
              learning_template_package_distribution_setting_id={distributionId}
              type={
                TtCsvExportType.SuperOwnerLearningPackageDistributionHistoryDetail
              }
            />
          </FormSectionWrap>
          <FormSectionWrap>
            {Array.from(accordionMainInfos.keys()).map((key) => {
              const students = accordionMainInfos.get(key); //
              const accordionDetails = handleGroupedData(
                students as DistributedStudents[],
                GroupeBy.Grade,
              );
              return (
                <Accordion key={key} className={classes.accordionRoot}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${key}-content`}
                    id={`${key}-header`}
                  >
                    {`${key} (${students?.length}人)`}
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container>
                      {Array.from(accordionDetails.keys()).map(
                        (detailKey: string) => {
                          const studentInfos = accordionDetails
                            .get(detailKey)
                            ?.map((student: DistributedStudents) => {
                              return {
                                studentId: student.id,
                                name: `${student.last_name} ${student.first_name}`,
                              };
                            });
                          return (
                            <Grid key={detailKey} xs={12} md={12} item>
                              <FormCategoryTitle>{detailKey}</FormCategoryTitle>
                              <ul className={classes.studentInfosWrapper}>
                                {studentInfos?.map((student) => (
                                  <li
                                    key={student.studentId}
                                    style={{ margin: "10px" }}
                                  >
                                    {student.name}
                                  </li>
                                ))}
                              </ul>
                            </Grid>
                          );
                        },
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </FormSectionWrap>
          <FormSectionWrap>
            <V2Pagination
              totalCount={totalCountFetched}
              perPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              handlePageTransition={handlePageTransition}
            />
          </FormSectionWrap>
        </Container>
      )}
    </GenericModal>
  );
};

export default DistributionInfosModal;
